import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { Button } from '@/components/ui/button';
import { X, Plus, Loader2, ImagePlus, Sparkles, Check, Vote, ShoppingBag, Users, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { PollType, PollVisibility } from '@/types/polls';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import confetti from 'canvas-confetti';
import { ImageCropEditor } from '@/components/ui/ImageCropEditor';
import { useTryOnLooks, TryOnLook } from '@/hooks/useTryOnLooks';
import { useSavedItems, SavedItem } from '@/hooks/useSavedItems';
import { VisibilityToggle } from './VisibilityToggle';
import { FriendPickerForPoll } from './FriendPickerForPoll';
import { VideoUploadPreview } from './VideoUploadPreview';
import { InlineVideoRecorder } from './InlineVideoRecorder';
// Tabs import removed - consolidated into side-by-side upload area
import { validateVideo, extractVideoThumbnail } from '@/utils/videoValidation';
import { Progress } from '@/components/ui/progress';
import { VideoIcon } from 'lucide-react';

// Hook for detecting taps vs scrolls in a carousel
function useTapDetection() {
  const startPos = useRef<{x: number;y: number;} | null>(null);
  const moved = useRef(false);
  const MOVE_THRESHOLD = 10;

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    startPos.current = { x: e.clientX, y: e.clientY };
    moved.current = false;
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!startPos.current) return;
    const dx = Math.abs(e.clientX - startPos.current.x);
    const dy = Math.abs(e.clientY - startPos.current.y);
    if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
      moved.current = true;
    }
  }, []);

  const wasTap = useCallback(() => {
    const result = !moved.current;
    startPos.current = null;
    moved.current = false;
    return result;
  }, []);

  return { onPointerDown, onPointerMove, wasTap };
}

interface CreatePollSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialImageUrl?: string;
  initialImageUrls?: string[]; // Multiple initial images (e.g. before/after from Body Gym)
  initialImageName?: string;
  initialVideoUrl?: string; // Optional video URL for animated polls
  initialLookId?: string; // Pre-select this look in the recent looks carousel
}

interface ImageFile {
  dataUrl: string;
  file: File;
}

// Auto-detect poll type based on image count
function getAutoDetectedPollType(imageCount: number): PollType | null {
  if (imageCount === 0) return null;
  if (imageCount === 1) return 'yes-no';
  if (imageCount === 2) return 'this-or-that';
  return 'pick-one'; // 3+ images
}

function getPollTypeLabel(type: PollType | null): string {
  switch (type) {
    case 'yes-no':return 'Yes / No';
    case 'this-or-that':return 'This or That';
    case 'pick-one':return 'Pick One';
    default:return '';
  }
}

function getPollTypeIcon(type: PollType | null): string {
  switch (type) {
    case 'yes-no':return '✅';
    case 'this-or-that':return '⚔️';
    case 'pick-one':return '🏆';
    default:return '📷';
  }
}

const MAX_IMAGES = 6;

// Video upload state interface
interface VideoUploadState {
  file: File;
  objectUrl: string;
  duration: number;
}

export function CreatePollSheet({ open, onOpenChange, initialImageUrl, initialImageUrls, initialImageName, initialVideoUrl, initialLookId }: CreatePollSheetProps) {
  const [caption, setCaption] = useState('');
  const [images, setImages] = useState<ImageFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Anonymous polls disabled for now
  const [visibility, setVisibility] = useState<PollVisibility>('public');
  const [allowedVoters, setAllowedVoters] = useState<string[]>([]);
  const [showFriendPicker, setShowFriendPicker] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [pendingCropFiles, setPendingCropFiles] = useState<File[]>([]);
  const [initialImageLoaded, setInitialImageLoaded] = useState(false);
  const [isLoadingInitialImage, setIsLoadingInitialImage] = useState(false);
  const [selectedLooks, setSelectedLooks] = useState<Set<string>>(new Set());
  const [selectedSavedItems, setSelectedSavedItems] = useState<Set<string>>(new Set());
  // Track which items are currently being fetched to prevent duplicate taps
  const [loadingItemIds, setLoadingItemIds] = useState<Set<string>>(new Set());

  // Video upload state
  const [mediaMode, setMediaMode] = useState<'photos' | 'video'>('photos');
  const [videoUpload, setVideoUpload] = useState<VideoUploadState | null>(null);
  const [isValidatingVideo, setIsValidatingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);

  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Tap detection for carousel items (distinguishes tap from scroll)
  const savedItemsTap = useTapDetection();
  const looksTap = useTapDetection();

  // Fetch recent try-on looks
  const [showAllLooks, setShowAllLooks] = useState(false);
  const { looks: recentLooks, totalCount: totalLooksCount, isLoading: isLoadingLooks, refetch: refetchLooks } = useTryOnLooks({ pageSize: showAllLooks ? 200 : 15 });

  // Fetch saved items
  const { items: savedItems, isLoading: isLoadingSavedItems, refetch: refetchSavedItems } = useSavedItems();

  // Auto-detected poll type based on current image count
  const detectedType = getAutoDetectedPollType(images.length);

  // Load initial image(s) when provided - bypass cropping since pre-selected images are ready
  useEffect(() => {
    // Determine the list of URLs to load
    const urlsToLoad = initialImageUrls?.length ? initialImageUrls : initialImageUrl ? [initialImageUrl] : [];
    if (urlsToLoad.length === 0 || !open || initialImageLoaded || images.length > 0) return;

    const loadInitialImages = async () => {
      setIsLoadingInitialImage(true);
      try {
        const loadedImages: ImageFile[] = [];

        for (const url of urlsToLoad) {
          try {
            let blob: Blob;

            // Check if URL is external (CORS-restricted)
            const isExternal = (() => {
              if (url.startsWith('data:') || url.startsWith('blob:')) return false;
              try {return new URL(url).origin !== window.location.origin;} catch {return true;}
            })();

            if (!isExternal) {
              const response = await fetch(url);
              if (!response.ok) throw new Error('Fetch failed');
              blob = await response.blob();
            } else {
              const proxyUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-image`;
              const response = await fetch(proxyUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
                },
                body: JSON.stringify({ url })
              });
              if (!response.ok) throw new Error(`Proxy fetch failed: ${response.status}`);
              blob = await response.blob();
            }

            const file = new File([blob], initialImageName || 'image.jpg', { type: blob.type || 'image/jpeg' });

            const dataUrl = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(blob);
            });

            // Tag with look ID only for the first image
            const taggedDataUrl = initialLookId && loadedImages.length === 0 ? dataUrl + `#lookId=${initialLookId}` : dataUrl;
            loadedImages.push({ dataUrl: taggedDataUrl, file });
          } catch (err) {
            console.error('Failed to load initial image:', url, err);
          }
        }

        if (loadedImages.length > 0) {
          setImages(loadedImages);
          if (initialLookId) {
            setSelectedLooks(new Set([initialLookId]));
          }
          setInitialImageLoaded(true);
        }
      } catch (error) {
        console.error('Failed to load initial images:', error);
      } finally {
        setIsLoadingInitialImage(false);
      }
    };
    loadInitialImages();
  }, [initialImageUrl, initialImageUrls, open, initialImageLoaded, images.length, initialImageName, initialLookId]);

  // Refetch looks and saved items when sheet opens to ensure fresh data
  useEffect(() => {
    if (open) {
      refetchLooks();
      refetchSavedItems();
    }
  }, [open, refetchLooks, refetchSavedItems]);

  // Reset state when sheet closes
  useEffect(() => {
    if (!open) {
      // Reset the form state when closing
      resetForm();
      setInitialImageLoaded(false);
    }
  }, [open]);

  // Get aspect ratio - all poll types display at 3:4 portrait in the feed
  const getCropAspectRatio = (): "1/1" | "3/4" | "4/3" | "16/9" => {
    return '3/4';
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const remaining = MAX_IMAGES - images.length;
      const filesToProcess = Array.from(files).slice(0, remaining);

      if (filesToProcess.length > 0) {
        // Queue all files for cropping, start with the first one
        setPendingCropFiles(filesToProcess.slice(1));
        setCropFile(filesToProcess[0]);
      }
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleCropSave = async (croppedBlob: Blob) => {
    // Convert blob to File and dataUrl
    const file = new File([croppedBlob], `cropped_${Date.now()}.jpg`, { type: 'image/jpeg' });
    const dataUrl = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(croppedBlob);
    });

    setImages((prev) => {
      if (prev.length >= MAX_IMAGES) return prev;
      return [...prev, { dataUrl, file }];
    });

    // Process next pending file or clear crop state
    if (pendingCropFiles.length > 0) {
      setCropFile(pendingCropFiles[0]);
      setPendingCropFiles((prev) => prev.slice(1));
    } else {
      setCropFile(null);
    }
  };

  const handleCropCancel = () => {
    // Skip current file, process next or clear
    if (pendingCropFiles.length > 0) {
      setCropFile(pendingCropFiles[0]);
      setPendingCropFiles((prev) => prev.slice(1));
    } else {
      setCropFile(null);
    }
  };

  const removeImage = (index: number) => {
    const imageToRemove = images[index];
    // Check if this was a look image and unselect it
    const lookIdMatch = imageToRemove?.dataUrl.match(/#lookId=([^&]+)/);
    if (lookIdMatch) {
      setSelectedLooks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(lookIdMatch[1]);
        return newSet;
      });
    }
    // Check if this was a saved item image and unselect it
    const savedItemIdMatch = imageToRemove?.dataUrl.match(/#savedItemId=([^&]+)/);
    if (savedItemIdMatch) {
      setSelectedSavedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(savedItemIdMatch[1]);
        return newSet;
      });
    }
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setCaption('');
    setImages([]);
    // isAnonymous removed
    setVisibility('public');
    setAllowedVoters([]);
    setCropFile(null);
    setPendingCropFiles([]);
    setSelectedLooks(new Set());
    setSelectedSavedItems(new Set());
    setLoadingItemIds(new Set());
    // Reset video state
    setMediaMode('photos');
    if (videoUpload?.objectUrl) {
      URL.revokeObjectURL(videoUpload.objectUrl);
    }
    setVideoUpload(null);
    setUploadProgress(0);
  };

  // Handle video file selection
  const handleVideoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input for re-selection
    e.target.value = '';

    setIsValidatingVideo(true);

    try {
      const validation = await validateVideo(file);

      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }

      // Clean up previous video URL
      if (videoUpload?.objectUrl) {
        URL.revokeObjectURL(videoUpload.objectUrl);
      }

      const objectUrl = URL.createObjectURL(file);
      setVideoUpload({
        file,
        objectUrl,
        duration: validation.duration || 0
      });

      // Video shown in UI as feedback
    } catch (error) {
      console.error('Video validation error:', error);
      toast.error('Could not process video');
    } finally {
      setIsValidatingVideo(false);
    }
  };

  // Handle recorded video from inline recorder
  const handleVideoRecorded = async (file: File, duration: number) => {
    // Clean up previous video URL
    if (videoUpload?.objectUrl) {
      URL.revokeObjectURL(videoUpload.objectUrl);
    }

    const objectUrl = URL.createObjectURL(file);
    setVideoUpload({
      file,
      objectUrl,
      duration
    });

    setShowVideoRecorder(false);
    // Video shown in UI as feedback
  };

  // Remove video
  const removeVideo = () => {
    if (videoUpload?.objectUrl) {
      URL.revokeObjectURL(videoUpload.objectUrl);
    }
    setVideoUpload(null);
  };

  // Upload video to storage
  const uploadVideo = async (): Promise<string | null> => {
    if (!videoUpload || !user) return null;

    const fileExt = videoUpload.file.name.split('.').pop() || 'mp4';
    const fileName = `${user.id}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round(e.loaded / e.total * 100);
          setUploadProgress(percent);
        }
      });

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const { data: { publicUrl } } = supabase.storage.
          from('poll-videos').
          getPublicUrl(fileName);
          resolve(publicUrl);
        } else {
          reject(new Error('Upload failed'));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed'));

      // Get the upload URL from Supabase
      supabase.storage.
      from('poll-videos').
      upload(fileName, videoUpload.file, { upsert: true }).
      then(({ error }) => {
        if (error) {
          reject(error);
        } else {
          const { data: { publicUrl } } = supabase.storage.
          from('poll-videos').
          getPublicUrl(fileName);
          resolve(publicUrl);
        }
      });
    });
  };

  // Helper to get the primary image URL for a saved item
  const getSavedItemImageUrl = (item: SavedItem): string | null => {
    return item.image_url || (item.image_urls?.length > 0 ? item.image_urls[0] : null);
  };

  // Handle selecting a saved item from the carousel
  const handleSavedItemSelect = async (item: SavedItem) => {
    // Prevent duplicate taps while loading
    if (loadingItemIds.has(item.id)) return;

    const imageUrl = getSavedItemImageUrl(item);
    if (!imageUrl) {
      toast.error('This item has no image');
      return;
    }

    // Check if already selected - handle deselection immediately (no async needed)
    if (selectedSavedItems.has(item.id)) {
      setSelectedSavedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
      // Remove this image from the images array
      setImages((prev) => prev.filter((img) => !img.dataUrl.includes(`#savedItemId=${item.id}`)));
      return;
    }

    if (images.length >= MAX_IMAGES) {
      toast.error(`You can only add up to ${MAX_IMAGES} images`);
      return;
    }

    // Mark as loading immediately to prevent duplicate taps
    setLoadingItemIds((prev) => new Set(prev).add(item.id));

    // Optimistically mark as selected for immediate visual feedback
    setSelectedSavedItems((prev) => new Set(prev).add(item.id));

    try {
      // Fetch and add the saved item image via proxy to avoid CORS issues
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();

      const proxyResponse = await fetch(
        `${supabaseUrl}/functions/v1/image-proxy?url=${encodeURIComponent(imageUrl)}&cache=true`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`
          }
        }
      );

      if (!proxyResponse.ok) {
        throw new Error('Proxy request failed');
      }

      const contentType = proxyResponse.headers.get('content-type');

      // Check if we got a JSON response (cached URL) or binary image data
      if (contentType?.includes('application/json')) {
        const result = await proxyResponse.json();
        if (result.url) {
          // Re-fetch the cached image
          const cachedResponse = await fetch(result.url);
          const blob = await cachedResponse.blob();
          const file = new File([blob], `saved_${item.id}.jpg`, { type: blob.type || 'image/jpeg' });
          const dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          const taggedDataUrl = dataUrl + `#savedItemId=${item.id}`;
          setImages((prev) => [...prev, { dataUrl: taggedDataUrl, file }]);
        } else {
          throw new Error('No cached URL returned');
        }
      } else {
        // Got binary image data directly
        const blob = await proxyResponse.blob();
        const file = new File([blob], `saved_${item.id}.jpg`, { type: blob.type || 'image/jpeg' });
        const dataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        const taggedDataUrl = dataUrl + `#savedItemId=${item.id}`;
        setImages((prev) => [...prev, { dataUrl: taggedDataUrl, file }]);
      }
    } catch (error) {
      console.error('Failed to load saved item image:', error);
      toast.error('Could not load the image');
      // Revert optimistic selection on error
      setSelectedSavedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    } finally {
      // Clear loading state
      setLoadingItemIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  // Handle selecting a look from the carousel
  const handleLookSelect = async (look: TryOnLook) => {
    // Prevent duplicate taps while loading
    if (loadingItemIds.has(look.id)) return;

    // Check if already selected - handle deselection immediately (no async needed)
    if (selectedLooks.has(look.id)) {
      setSelectedLooks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(look.id);
        return newSet;
      });
      // Remove this image from the images array
      setImages((prev) => prev.filter((img) => !img.dataUrl.includes(look.id)));
      return;
    }

    if (images.length >= MAX_IMAGES) {
      toast.error(`You can only add up to ${MAX_IMAGES} images`);
      return;
    }

    // Mark as loading immediately to prevent duplicate taps
    setLoadingItemIds((prev) => new Set(prev).add(look.id));

    // Optimistically mark as selected for immediate visual feedback
    setSelectedLooks((prev) => new Set(prev).add(look.id));

    try {
      let blob: Blob;

      // Try direct fetch first (works for Supabase storage URLs)
      try {
        const response = await fetch(look.result_image_url);
        if (!response.ok) throw new Error('Direct fetch failed');
        blob = await response.blob();
      } catch {
        // Fallback to proxy for external URLs
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const { data: { session } } = await supabase.auth.getSession();

        const proxyResponse = await fetch(
          `${supabaseUrl}/functions/v1/image-proxy?url=${encodeURIComponent(look.result_image_url)}&cache=true`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${session?.access_token}`
            }
          }
        );

        if (!proxyResponse.ok) throw new Error('Proxy fetch failed');

        const contentType = proxyResponse.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const result = await proxyResponse.json();
          if (result.url) {
            const cachedResponse = await fetch(result.url);
            blob = await cachedResponse.blob();
          } else {
            throw new Error('No cached URL');
          }
        } else {
          blob = await proxyResponse.blob();
        }
      }

      const file = new File([blob], `look_${look.id}.jpg`, { type: blob.type || 'image/jpeg' });
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
      // Tag the dataUrl with the look id for tracking
      const taggedDataUrl = dataUrl + `#lookId=${look.id}`;
      setImages((prev) => [...prev, { dataUrl: taggedDataUrl, file }]);
    } catch (error) {
      console.error('Failed to load look image:', error);
      toast.error('Could not load the image');
      // Revert optimistic selection on error
      setSelectedLooks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(look.id);
        return newSet;
      });
    } finally {
      // Clear loading state
      setLoadingItemIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(look.id);
        return newSet;
      });
    }
  };

  const canSubmit = () => {
    if (!caption.trim()) return false;
    if (!user) return false;
    // For private polls, require at least one friend selected
    if (visibility === 'private' && allowedVoters.length === 0) return false;

    // In video mode, require a video
    if (mediaMode === 'video') {
      return videoUpload !== null;
    }

    // In photos mode, require at least one image
    return images.length >= 1;
  };

  const getValidationMessage = () => {
    if (!user) return 'Sign in to create a poll';
    if (mediaMode === 'video' && !videoUpload) return 'Add a video';
    if (mediaMode === 'photos' && images.length === 0) return 'Add at least one image';
    if (!caption.trim()) return 'Add a question';
    if (visibility === 'private' && allowedVoters.length === 0) return 'Select at least one friend';
    return '';
  };

  const getPlaceholder = () => {
    if (mediaMode === 'video') return 'What do you think of this?';
    if (images.length === 0) return 'What do you want to ask?';
    if (images.length === 1) return 'Should I get this?';
    if (images.length === 2) return 'Which one should I get?';
    return 'Which is your favorite?';
  };

  // Get poll type - video polls are always yes-no
  const getEffectivePollType = (): PollType | null => {
    if (mediaMode === 'video') return 'yes-no';
    return detectedType;
  };

  const uploadImages = async (): Promise<{id: string;url: string;}[]> => {
    if (!user) throw new Error('Not authenticated');

    const uploadedImages: {id: string;url: string;}[] = [];

    for (const image of images) {
      const rawExt = image.file.name.split('.').pop() || '';
      const fileExt = /^[a-zA-Z0-9]{1,5}$/.test(rawExt) ? rawExt.toLowerCase() : 'jpg';
      const fileName = `${user.id}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage.
      from('poll-images').
      upload(fileName, image.file, { upsert: true });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error(`Failed to upload image: ${uploadError.message}`);
      }

      const { data: { publicUrl } } = supabase.storage.
      from('poll-images').
      getPublicUrl(fileName);

      uploadedImages.push({
        id: fileName,
        url: publicUrl
      });
    }

    return uploadedImages;
  };

  const handleSubmit = async () => {
    const effectiveType = getEffectivePollType();
    if (!canSubmit() || !user || !effectiveType) {
      toast.error(getValidationMessage());
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      let uploadedImages: {id: string;url: string;}[] = [];
      
      // Resolve video URL: use initialVideoUrl if provided, otherwise fetch from look
      let videoUrl: string | null = initialVideoUrl || null;
      if (!videoUrl && initialLookId) {
        // Fetch animated video URL directly from the look (handles stale parent data)
        const { data: lookData } = await supabase
          .from('tryon_looks')
          .select('animated_video_url')
          .eq('id', initialLookId)
          .maybeSingle();
        if (!lookData?.animated_video_url) {
          // Also check body_gym_looks
          const { data: gymData } = await supabase
            .from('body_gym_looks')
            .select('animated_video_url')
            .eq('id', initialLookId)
            .maybeSingle();
          videoUrl = gymData?.animated_video_url || null;
        } else {
          videoUrl = lookData.animated_video_url;
        }
      }

      if (mediaMode === 'video' && videoUpload) {
        // For video mode, extract thumbnail and upload video
        const thumbnailBlob = await extractVideoThumbnail(videoUpload.file);
        const thumbnailFile = new File([thumbnailBlob], `thumbnail_${Date.now()}.jpg`, { type: 'image/jpeg' });

        // Upload thumbnail as the poll image
        const thumbFileName = `${user.id}/${Date.now()}_thumb.jpg`;
        const { error: thumbError } = await supabase.storage.
        from('poll-images').
        upload(thumbFileName, thumbnailFile, { upsert: true });

        if (thumbError) {
          throw new Error(`Failed to upload thumbnail: ${thumbError.message}`);
        }

        const { data: { publicUrl: thumbUrl } } = supabase.storage.
        from('poll-images').
        getPublicUrl(thumbFileName);

        uploadedImages = [{ id: thumbFileName, url: thumbUrl }];

        // Upload video
        const videoFileName = `${user.id}/${Date.now()}_${Math.random().toString(36).substring(7)}.mp4`;
        const { error: videoError } = await supabase.storage.
        from('poll-videos').
        upload(videoFileName, videoUpload.file, { upsert: true });

        if (videoError) {
          throw new Error(`Failed to upload video: ${videoError.message}`);
        }

        const { data: { publicUrl: uploadedVideoUrl } } = supabase.storage.
        from('poll-videos').
        getPublicUrl(videoFileName);

        videoUrl = uploadedVideoUrl;
      } else {
        // Upload images to storage
        uploadedImages = await uploadImages();
      }

      // Create the poll in the database and get the new poll ID
      const { data: newPoll, error: pollError } = await supabase.
      from('polls').
      insert({
        user_id: user.id,
        poll_type: effectiveType,
        caption: caption.trim(),
        images: uploadedImages,
        votes: {},
        is_anonymous: false, // Anonymous polls disabled
        video_url: videoUrl,
        visibility: visibility,
        allowed_voters: visibility === 'private' ? allowedVoters : null
      }).
      select('id').
      single();

      if (pollError) {
        console.error('Poll creation error:', pollError);
        throw new Error(`Failed to create poll: ${pollError.message}`);
      }

      // Optimistically inject the new poll into cached feed lists so it appears instantly after navigation
      if (newPoll?.id) {
        queryClient.setQueriesData({ queryKey: ['feed-polls'] }, (old: any) => {
          if (!Array.isArray(old)) return old;
          if (old.some((p: any) => p?.id === newPoll.id)) return old;

          const optimisticPoll = {
            id: newPoll.id,
            author: {
              id: user.id,
              username: user.user_metadata?.display_name || user.email?.split('@')[0] || 'You',
              avatar: user.user_metadata?.avatar_url || '',
            },
            pollType: effectiveType,
            caption: caption.trim(),
            images: uploadedImages,
            videoUrl: videoUrl || undefined,
            votes: {},
            timestamp: 'just now',
            createdAt: new Date().toISOString(),
            isSponsored: false,
            isOfficial: false,
          };

          return [optimisticPoll, ...old];
        });
      }

      // Invalidate and AWAIT feed-polls refetch so backend state catches up immediately
      queryClient.invalidateQueries({ queryKey: ['public-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-polls'] });
      await queryClient.refetchQueries({ queryKey: ['feed-polls'] });

      // Update persona signals for poll creation (fire and forget)
      supabase.functions.invoke('update-persona-signal', {
        body: {
          type: 'poll_create',
          data: { poll_type: effectiveType, visibility, has_video: mediaMode === 'video' }
        }
      }).catch(console.error);

      // Celebrate with confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7']
      });

      // Show appropriate toast based on visibility
      // Confetti provides visual feedback - no toast needed

      resetForm();
      onOpenChange(false);

      // Navigate to the vote page with the new poll highlighted
      if (newPoll?.id) {
        navigate(`/vote?poll=${newPoll.id}`);
      }
    } catch (error) {
      console.error('Error creating poll:', error);
      toast.error(error instanceof Error ? error.message : 'Something went wrong');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Show crop editor if a file is being cropped
  if (cropFile) {
    return (
      <ImageCropEditor
        imageFile={cropFile}
        onSave={handleCropSave}
        onCancel={handleCropCancel}
        aspectRatio={getCropAspectRatio()}
        hint="Crop your poll image" />);


  }

  const headerContent =
  <div className="flex items-center justify-between px-4 pb-3">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Vote className="w-5 h-5 text-primary" />
        Ask the Community
      </h2>
      <Button
      variant="ghost"
      size="icon"
      onClick={() => onOpenChange(false)}
      className="h-8 w-8">

        <X className="h-4 w-4" />
      </Button>
    </div>;


  // Single-step poll creation form
  return (
    <AnimatePresence>
      {open &&
      <DraggableBottomSheet
        open={open}
        onOpenChange={onOpenChange}
        height="85vh"
        customHeader={headerContent}>

          <div className="px-4 space-y-6 overflow-y-auto pb-10">
            {/* Sign in prompt */}
            {!user &&
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <p className="text-sm text-amber-600 dark:text-amber-400 text-center">
                  Sign in to create and share polls
                </p>
              </div>
          }

            {/* Media Upload Area */}
            <div>
              {/* Empty state: no photos and no video selected - show side-by-side options */}
              {images.length === 0 && !videoUpload && !isLoadingInitialImage && !isValidatingVideo && !showVideoRecorder && mediaMode === 'photos' ?
            <div className="grid grid-cols-2 gap-3">
                  {/* Add Photos */}
                  <label className={cn(
                "flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 border-dashed border-muted-foreground/25 transition-colors",
                isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-primary/50 hover:bg-accent"
              )}>
                    <ImagePlus className="w-8 h-8 text-muted-foreground" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-foreground">Add photos</p>
                      <p className="text-xs text-muted-foreground mt-0.5">1-6 images</p>
                    </div>
                    <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={isSubmitting}
                  className="hidden" />

                  </label>

                  {/* Add Video */}
                  <button
                onClick={() => setMediaMode('video')}
                disabled={isSubmitting}
                className={cn(
                  "flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 border-dashed border-muted-foreground/25 transition-colors",
                  isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:border-primary/50 hover:bg-accent"
                )}>

                    <Video className="w-8 h-8 text-muted-foreground" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-foreground">Add video</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Record or upload</p>
                    </div>
                  </button>
                </div> :
            mediaMode === 'photos' ?
            <>
                  {images.length > 0 &&
              <p className="text-sm font-medium text-foreground mb-2">
                      {images.length}/{MAX_IMAGES} photos
                      <button
                  onClick={() => {setImages([]);setSelectedLooks(new Set());setSelectedSavedItems(new Set());setMediaMode('photos');}}
                  className="text-xs text-muted-foreground ml-2 underline">

                        switch to video
                      </button>
                    </p>
              }
                  
                  {isLoadingInitialImage ?
              <div className="flex flex-col items-center justify-center gap-3 p-8 rounded-2xl border-2 border-dashed border-muted-foreground/30">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-foreground">Loading image...</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Preparing your try-on result
                        </p>
                      </div>
                    </div> :
              images.length === 0 ?
              <label className={cn(
                "flex flex-col items-center justify-center gap-3 p-8 rounded-2xl border-2 border-dashed border-muted-foreground/30 transition-colors",
                isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-primary/50 hover:bg-surface-2"
              )}>
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <ImagePlus className="w-8 h-8 text-primary" />
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-foreground">Tap to add photos</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Add 1-6 photos to create your poll
                        </p>
                      </div>
                      <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={isSubmitting}
                  className="hidden" />

                    </label> :

              // Images added - thumbnail grid with padding for delete buttons
              <div className="flex gap-3 overflow-x-auto pb-2 pt-2 px-1 -mx-1">
                  {images.map((img, index) =>
                <div key={index} className="relative flex-shrink-0">
                      <img
                    src={img.dataUrl.split('#')[0]}
                    alt={`Upload ${index + 1}`}
                    className="w-24 h-24 object-cover rounded-xl" />

                      <button
                    onClick={() => removeImage(index)}
                    disabled={isSubmitting}
                    className="absolute -top-1.5 -right-1.5 w-7 h-7 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center disabled:opacity-50 shadow-md">

                        <X className="w-4 h-4" />
                      </button>
                    </div>
                )}
                  
                  {images.length < MAX_IMAGES &&
                <label className={cn(
                  "flex-shrink-0 w-24 h-24 bg-surface-2 rounded-xl flex flex-col items-center justify-center transition-colors",
                  isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-surface-3"
                )}>
                      <Plus className="w-6 h-6 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground mt-1">Add</span>
                      <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={isSubmitting}
                    className="hidden" />

                    </label>
                }
                </div>
              }
                </> : (

            /* Video Upload Mode */
            <>
                  {!videoUpload && !isValidatingVideo && !showVideoRecorder &&
              <p className="text-sm font-medium text-foreground mb-2">
                      <button
                  onClick={() => {setMediaMode('photos');}}
                  className="text-xs text-muted-foreground underline">

                        ← switch to photos
                      </button>
                    </p>
              }
                  {isValidatingVideo ?
              <div className="flex flex-col items-center justify-center gap-3 p-8 rounded-2xl border-2 border-dashed border-muted-foreground/30">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-foreground">Checking video...</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Validating duration and size
                        </p>
                      </div>
                    </div> :
              videoUpload ?
              <div className="space-y-3">
                      <VideoUploadPreview
                  file={videoUpload.file}
                  videoUrl={videoUpload.objectUrl}
                  duration={videoUpload.duration}
                  onRemove={removeVideo}
                  disabled={isSubmitting} />

                      {isSubmitting && uploadProgress > 0 &&
                <div className="space-y-1">
                          <Progress value={uploadProgress} className="h-2" />
                          <p className="text-xs text-muted-foreground text-center">
                            Uploading video... {uploadProgress}%
                          </p>
                        </div>
                }
                    </div> :

              <div className="flex flex-col gap-4">
                      {showVideoRecorder ? (
                /* Inline Video Recorder */
                <InlineVideoRecorder
                  onVideoRecorded={handleVideoRecorded}
                  onCancel={() => setShowVideoRecorder(false)}
                  disabled={isSubmitting} />) :


                <>
                          {/* Record Button - Primary */}
                          <button
                    onClick={() => setShowVideoRecorder(true)}
                    disabled={isSubmitting}
                    className={cn(
                      "flex flex-col items-center justify-center gap-3 p-8 rounded-2xl border-2 border-primary bg-primary/5 transition-colors",
                      isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:bg-primary/10 active:scale-[0.98]"
                    )}>

                            <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center">
                              <div className="w-5 h-5 rounded-full bg-white" />
                            </div>
                            <div className="text-center">
                              <p className="font-medium text-foreground">Record video</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                Up to 1 minute
                              </p>
                            </div>
                          </button>

                          {/* Upload Divider */}
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-px bg-border" />
                            <span className="text-xs text-muted-foreground">or upload a file</span>
                            <div className="flex-1 h-px bg-border" />
                          </div>

                          {/* File Upload - Secondary */}
                          <label className={cn(
                    "flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-border transition-colors",
                    isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-surface-2"
                  )}>
                            <Video className="w-5 h-5 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Upload from library</span>
                            <input
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm"
                      onChange={handleVideoSelect}
                      disabled={isSubmitting}
                      className="hidden" />

                          </label>
                        </>
                }
                    </div>
              }
                </>)
            }
            </div>

            {/* Recent Looks Carousel - only show in photos mode */}
            {user && mediaMode === 'photos' &&
          <div>
                <label className="text-sm font-medium text-foreground mb-2 block flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Or use a recent look
                </label>
                {isLoadingLooks ?
            // Skeleton loader - matches exact dimensions of look thumbnails
            <div className="flex gap-3 overflow-x-auto pb-2 pt-1 px-1 -mx-1">
                    {[1, 2, 3, 4].map((i) =>
              <div
                key={i}
                className="flex-shrink-0 w-20 h-28 rounded-xl bg-muted animate-pulse" />

              )}
                  </div> :
            recentLooks.length > 0 ?
            <div className="flex gap-3 overflow-x-auto pb-2 pt-1 px-1 -mx-1">
                    {recentLooks.map((look) => {
                const isSelected = selectedLooks.has(look.id);
                const isLoading = loadingItemIds.has(look.id);
                return (
                  <button
                    key={look.id}
                    onPointerDown={looksTap.onPointerDown}
                    onPointerMove={looksTap.onPointerMove}
                    onPointerUp={() => {
                      if (looksTap.wasTap() && !isLoading) {
                        handleLookSelect(look);
                      }
                    }}
                    disabled={isSubmitting || isLoading || images.length >= MAX_IMAGES && !isSelected}
                    className={cn(
                      "relative flex-shrink-0 rounded-xl overflow-hidden transition-all touch-manipulation",
                      isSelected ?
                      "ring-2 ring-primary ring-offset-2 ring-offset-background" :
                      "active:opacity-80",
                      images.length >= MAX_IMAGES && !isSelected && "opacity-40 cursor-not-allowed",
                      isLoading && "opacity-70"
                    )}>

                          <img
                      src={look.result_image_url}
                      alt="Try-on look"
                      className="w-20 h-28 object-cover pointer-events-none" />

                          {/* Loading indicator */}
                          {isLoading &&
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
                              <Loader2 className="w-5 h-5 text-white animate-spin" />
                            </div>
                    }
                          {/* Selected checkmark (show even when loading) */}
                          {isSelected && !isLoading &&
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center pointer-events-none">
                              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                <Check className="w-4 h-4 text-primary-foreground" />
                              </div>
                            </div>
                    }
                        </button>);

              })}
                    {/* See all button when there are more looks */}
                    {!showAllLooks && totalLooksCount > 15 && (
                      <button
                        onClick={() => setShowAllLooks(true)}
                        className="flex-shrink-0 w-20 h-28 rounded-xl bg-surface-2 border border-border/50 flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-3 transition-colors active:scale-95"
                      >
                        <span className="text-lg font-semibold">{totalLooksCount}</span>
                        <span className="text-[10px] font-medium">See all</span>
                      </button>
                    )}
                  </div> :

            // Empty state - no looks yet
            <div className="flex items-center gap-3 p-3 rounded-xl bg-surface-2 border border-border/50">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">No looks yet</p>
                      <p className="text-xs text-muted-foreground">Try on outfits to see them here!</p>
                    </div>
                  </div>
            }
              </div>
          }

            {/* Saved Items Carousel - only show in photos mode */}
            {user && mediaMode === 'photos' &&
          <div>
                <label className="text-sm font-medium text-foreground mb-2 block flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4 text-primary" />
                  Or use a saved item
                </label>
                {isLoadingSavedItems ?
            // Skeleton loader
            <div className="flex gap-3 overflow-x-auto pb-2 pt-1 px-1 -mx-1">
                    {[1, 2, 3, 4].map((i) =>
              <div
                key={i}
                className="flex-shrink-0 w-20 h-28 rounded-xl bg-muted animate-pulse" />

              )}
                  </div> :
            savedItems.filter((item) => getSavedItemImageUrl(item)).length > 0 ?
            <div className="flex gap-3 overflow-x-auto pb-2 pt-1 px-1 -mx-1">
                    {[...savedItems.filter((item) => getSavedItemImageUrl(item))].slice(0, 10).map((item) => {
                const isSelected = selectedSavedItems.has(item.id);
                const isLoading = loadingItemIds.has(item.id);
                const imageUrl = getSavedItemImageUrl(item)!;
                return (
                  <button
                    key={item.id}
                    onPointerDown={savedItemsTap.onPointerDown}
                    onPointerMove={savedItemsTap.onPointerMove}
                    onPointerUp={() => {
                      if (savedItemsTap.wasTap() && !isLoading) {
                        handleSavedItemSelect(item);
                      }
                    }}
                    disabled={isSubmitting || isLoading || images.length >= MAX_IMAGES && !isSelected}
                    className={cn(
                      "relative flex-shrink-0 rounded-xl overflow-hidden transition-all touch-manipulation",
                      isSelected ?
                      "ring-2 ring-primary ring-offset-2 ring-offset-background" :
                      "active:opacity-80",
                      images.length >= MAX_IMAGES && !isSelected && "opacity-40 cursor-not-allowed",
                      isLoading && "opacity-70"
                    )}>

                          <img
                      src={imageUrl}
                      alt={item.name || "Saved item"}
                      className="w-20 h-28 object-cover pointer-events-none" />

                          {/* Loading indicator */}
                          {isLoading &&
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
                              <Loader2 className="w-5 h-5 text-white animate-spin" />
                            </div>
                    }
                          {/* Selected checkmark (show even when loading) */}
                          {isSelected && !isLoading &&
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center pointer-events-none">
                              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                <Check className="w-4 h-4 text-primary-foreground" />
                              </div>
                            </div>
                    }
                        </button>);

              })}
                  </div> :

            // Empty state - no saved items
            <div className="flex items-center gap-3 p-3 rounded-xl bg-surface-2 border border-border/50">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <ShoppingBag className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">No saved items yet</p>
                      <p className="text-xs text-muted-foreground">Save products to see them here!</p>
                    </div>
                  </div>
            }
              </div>
          }

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-foreground">
                  Your question
                </label>
              </div>
              <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={getPlaceholder()}
              disabled={isSubmitting}
              className="w-full h-24 bg-surface-2 rounded-xl px-4 py-3 text-base text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50" />

            </div>

            {/* Visibility Toggle */}
            <VisibilityToggle
            value={visibility}
            onChange={(newVisibility) => {
              setVisibility(newVisibility);
              // Auto-open friend picker when switching to private
              if (newVisibility === 'private' && allowedVoters.length === 0) {
                setShowFriendPicker(true);
              }
            }}
            disabled={isSubmitting} />


            {/* Selected Friends Preview (for private polls) */}
            {visibility === 'private' &&
          <button
            type="button"
            onClick={() => setShowFriendPicker(true)}
            disabled={isSubmitting}
            className={cn(
              "w-full flex items-center justify-between p-4 rounded-xl border-2 transition-colors",
              allowedVoters.length > 0 ?
              "border-primary/30 bg-primary/5" :
              "border-border bg-surface-2 hover:border-muted-foreground/50",
              isSubmitting && "opacity-50 cursor-not-allowed"
            )}>

                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className={allowedVoters.length > 0 ? "text-foreground font-medium" : "text-muted-foreground"}>
                    {allowedVoters.length > 0 ?
                `${allowedVoters.length} friend${allowedVoters.length > 1 ? 's' : ''} selected` :
                'Select friends to invite'
                }
                  </span>
                </div>
                <span className="text-primary text-sm font-medium">
                  {allowedVoters.length > 0 ? 'Change' : 'Select'}
                </span>
              </button>
          }

            {/* Validation message */}
            {!canSubmit() && images.length > 0 &&
          <p className="text-sm text-amber-500">
                {getValidationMessage()}
              </p>
          }

            {/* Submit Button */}
            <button
            onClick={handleSubmit}
            disabled={!canSubmit() || isSubmitting}
            className={cn(
              "w-full py-4 rounded-2xl font-semibold text-base transition-all flex items-center justify-center gap-2",
              canSubmit() && !isSubmitting ?
              "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.98]" :
              "bg-muted text-muted-foreground cursor-not-allowed"
            )}>

              {isSubmitting ?
            <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Posting...
                </> :

            <>
                  <Vote className="w-5 h-5" />
                  Ask
                </>
            }
            </button>
          </div>
        </DraggableBottomSheet>
      }

      {/* Friend Picker for Private Polls */}
      <FriendPickerForPoll
        open={showFriendPicker}
        onOpenChange={setShowFriendPicker}
        selectedFriends={allowedVoters}
        onSelectionChange={setAllowedVoters} />


    </AnimatePresence>);

}